import { jsx } from 'panel';
import range from 'lodash/range';
import { ItemsMenuEl } from 'common/components/items-menu';
import { InputEl } from 'common/components/input';
import { ChartLegendTheme, MAX_SEGMENTS_PER_COMPACT_TABLE_ROW } from './constants';

function LegendLoader() {
  return jsx("div", {
    sel: '.loader'
  }, range(9).map((_, idx) => jsx("div", {
    sel: '.mp-skeleton-large',
    key: idx
  })));
}

function ReadOnlyLegend(_ref) {
  let {
    selectedSections
  } = _ref;
  return jsx("div", {
    sel: '.legend-container.legend-readonly'
  }, selectedSections.map((section, idx) => jsx(null, null, idx > 0 ? jsx("div", {
    sel: '.section-divider'
  }) : null, section.items.map((item, idx) => jsx("div", {
    sel: '.legend-segment',
    key: idx
  }, jsx("div", {
    sel: '.segment-icon'
  }, jsx("div", {
    sel: '.readonly-item-color',
    style: {
      'background-color': item.color
    }
  })), jsx("div", {
    sel: '.segment-text'
  }, jsx("span", null, item.label)))))));
}

function CardTableLegend(_ref2) {
  let {
    sections
  } = _ref2;
  // Only allow one section for card legends
  const {
    items
  } = sections[0];
  const rows = Math.ceil(items.length / MAX_SEGMENTS_PER_COMPACT_TABLE_ROW);
  const segmentsPerRow = Math.ceil(items.length / rows);
  return jsx("div", {
    sel: '.legend-container.legend-table'
  }, range(rows).map(rowIdx => jsx("div", {
    sel: '.legend-line',
    key: rowIdx
  }, range(segmentsPerRow).map(colIdx => {
    const item = items[colIdx + segmentsPerRow * rowIdx];
    return item ? jsx("div", {
      sel: '.legend-segment',
      key: colIdx
    }, item.label ? jsx("div", {
      sel: '.segment-text',
      attrs: {
        title: item.label
      },
      style: {
        '--color': item.color
      }
    }, item.label) : null, item.tooltipSections && jsx("div", {
      sel: '.legend-rollup-segments'
    }, item.tooltipSections.map((_ref3) => {
      let {
        color,
        label
      } = _ref3;
      return jsx("div", {
        sel: '.segment-text',
        style: {
          '--color': color
        }
      }, label);
    }))) : null;
  }))));
}

function CardListLegend(_ref4) {
  let {
    sections
  } = _ref4;
  // Only allow one section for card legends
  const {
    items
  } = sections[0];
  return jsx("div", {
    sel: '.legend-container.legend-list'
  }, items.map((item, idx) => {
    const text = item.label;
    return jsx("div", {
      sel: '.legend-segment',
      key: idx
    }, jsx("div", {
      sel: '.segment-text',
      style: {
        '--color': item.color
      },
      attrs: {
        title: text
      }
    }, text));
  }));
}

function PrimaryLegend(_ref5) {
  let {
    sections,
    searchQuery,
    searchInput,
    showSelectAll,
    selectAllDisabled,
    selectAllDisabledTooltip,
    height,
    onClickItem,
    onSelectAll
  } = _ref5;
  return jsx("div", {
    sel: '.chart-legend-container'
  }, jsx(ItemsMenuEl, {
    style: {
      'max-height': height
    },
    attrs: {
      elref: "itemsMenu",
      multiple: true,
      'search-filter': searchQuery,
      sections,
      'show-select-all': showSelectAll,
      'select-all-disabled': selectAllDisabled,
      'select-all-disabled-tooltip': selectAllDisabledTooltip
    },
    on: {
      clickItem: onClickItem,
      selectAll: onSelectAll
    },
    hook: {
      insert: (_ref6) => {
        let {
          elm
        } = _ref6;
        return elm.keydownProxyEl = searchInput;
      }
    }
  }));
}

export function template() {
  const {
    searchQuery
  } = this.state;
  const sections = this.attr("sections") || this.helpers.getSections();
  const loading = this.attr("loading");
  const theme = this.attr("theme");
  let themedLegend;

  switch (theme) {
    case ChartLegendTheme.ReadOnly:
      themedLegend = jsx(ReadOnlyLegend, {
        selectedSections: this.helpers.filterSelected(sections)
      });
      break;

    case ChartLegendTheme.CardList:
      themedLegend = jsx(CardListLegend, {
        sections: sections
      });
      break;

    case ChartLegendTheme.CardTable:
      themedLegend = jsx(CardTableLegend, {
        sections: sections
      });
      break;

    default:
      themedLegend = jsx(PrimaryLegend, {
        sections: sections,
        searchQuery: searchQuery,
        searchInput: this.elRefs.searchInput,
        showSelectAll: this.attr("show-select-all"),
        selectAllDisabled: this.attr("select-all-disabled"),
        selectAllDisabledTooltip: this.attr("select-all-disabled-tooltip"),
        height: this.helpers.getItemsMenuHeight(),
        onClickItem: this.helpers.toggledCheckbox,
        onSelectAll: this.helpers.toggledAllCheckboxes
      });
  }

  return jsx("div", {
    sel: '.chart-legend',
    class: {
      ["chart-theme-".concat(theme)]: true
    },
    style: this.helpers.overrideStyle(),
    attrs: {
      elref: "wrapper"
    }
  }, theme === ChartLegendTheme.Primary && this.attr("enable-search") ? jsx("div", {
    sel: '.chart-legend-search'
  }, jsx(InputEl, {
    attrs: {
      icon: "search",
      placeholder: "Search",
      elref: "search"
    },
    hook: {
      insert: (_ref7) => {
        let {
          elm
        } = _ref7;
        return this.setElRef("searchInput", elm);
      }
    },
    on: {
      input: this.helpers.updateLegendSearch
    }
  })) : null, loading ? jsx(LegendLoader, null) : themedLegend);
}